'use client'

import { useCustomTokens } from '@sushiswap/hooks'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  type EvmAddress,
  type EvmChainId,
  EvmToken,
  isEvmAddress,
  isEvmChainId,
} from 'sushi/evm'
import { useConfig } from 'wagmi'
import { getToken } from '../../../actions/getToken'
import {
  type KatanaTokenListEntry,
  fetchKatanaTokenList,
} from './katana-token-list'
import { searchSubgraphToken } from './search-subgraph-token'

interface UseSearchTokens {
  chainId: EvmChainId | undefined
  search?: string
}

export function useSearchTokens({ chainId, search }: UseSearchTokens) {
  const config = useConfig()
  const { data: customTokens } = useCustomTokens()

  const query = useQuery({
    queryKey: ['search-tokens', { chainId, search }],
    queryFn: async () => {
      if (!chainId) throw new Error('chainId is required')

      const tokenList = await fetchKatanaTokenList()
      const query = (search || '').trim().toLowerCase()

      // 1. Client-side filter on Katana token list + custom tokens.
      // Dedupe by address (prefer the Katana token list entry) so the same
      // token doesn't render twice when a user-imported custom token shadows
      // an entry from the official list.
      const allTokens: KatanaTokenListEntry[] = []
      const seen = new Set<string>()
      for (const t of tokenList) {
        const addr = t.address.toLowerCase()
        if (seen.has(addr)) continue
        seen.add(addr)
        allTokens.push(t)
      }
      for (const t of Object.values(customTokens)) {
        if (t.chainId !== chainId) continue
        const addr = t.address.toLowerCase()
        if (seen.has(addr)) continue
        seen.add(addr)
        allTokens.push({
          address: t.address,
          name: t.name || '',
          symbol: t.symbol || '',
          decimals: t.decimals,
          approved: false,
        })
      }

      if (!query) {
        return toEvmTokens(allTokens, chainId)
      }

      const filtered = allTokens.filter(
        (t) =>
          t.symbol.toLowerCase().includes(query) ||
          t.name.toLowerCase().includes(query) ||
          t.address.toLowerCase() === query,
      )

      if (filtered.length > 0) {
        return toEvmTokens(filtered, chainId)
      }

      // 2. If search is an address, try the V3 subgraph
      if (search && isEvmAddress(search)) {
        const subgraphToken = await searchSubgraphToken(search as EvmAddress)
        if (subgraphToken) {
          return toEvmTokens([subgraphToken], chainId)
        }

        // 3. RPC fallback via multicall
        try {
          const rpcToken = await getToken(config, {
            address: search as EvmAddress,
            chainId,
          })
          return [
            new EvmToken({
              chainId,
              address: rpcToken.address,
              name: rpcToken.name || '',
              symbol: rpcToken.symbol || '',
              decimals: rpcToken.decimals,
              metadata: { approved: false },
            }),
          ]
        } catch {
          // Not a valid token contract
        }
      }

      return []
    },
    enabled: !!chainId,
    staleTime: 15 * 60 * 1000,
  })

  const data = query.data as EvmToken[] | undefined

  return useMemo(
    () => ({
      ...query,
      data,
      hasMore: false,
      fetchNextPage: () => {},
    }),
    [query, data],
  )
}

function toEvmTokens(
  tokens: KatanaTokenListEntry[],
  chainId: EvmChainId,
): EvmToken[] {
  return tokens.map(
    (t) =>
      new EvmToken({
        chainId,
        address: t.address as EvmAddress,
        name: t.name,
        symbol: t.symbol,
        decimals: t.decimals,
        metadata: { approved: t.approved },
      }),
  )
}
